// Copyright 2016 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { CaptureDevice } from "datatypes/SoramaLocalApi";
import { DMADAQModuleRepository, MicrophoneArrayRackModuleRepository } from "repositories/HardwareModuleRepository";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";
import { ManagementHelperFunctions } from "services/Hardware/Management/ManagementHelperFunctions";

@autoinject
export class RemoteMeasurementDeviceManagement {
    private frontEndIdToModuleIds: { [id: string]: string } = {};

    // Hardware Repositories
    private microphoneArrayRackModuleRepository: MicrophoneArrayRackModuleRepository;
    private dmadaqModuleRepository: DMADAQModuleRepository;

    private pendingCall: Promise<MeasurementDevice[]> = null;

    constructor(
        dmadaqModuleRepository: DMADAQModuleRepository,
        microphoneArrayRackModuleRepository: MicrophoneArrayRackModuleRepository
    ) {
        this.microphoneArrayRackModuleRepository = microphoneArrayRackModuleRepository;
        this.dmadaqModuleRepository = dmadaqModuleRepository;
    }

    public getRegisteredMeasurementDevices(measurementDevices: MeasurementDevice[]): Promise<MeasurementDevice[]> {
        // Do nothing when asked nothing.
        if (measurementDevices.length === 0) {
            return Promise.resolve([]);
        }

        if (this.pendingCall) {
            return this.pendingCall;
        }

        const devicesPromise: Promise<MeasurementDevice>[] = [];

        measurementDevices.map((device) => {
            devicesPromise.push(this.getAccessibleMeasurementDevice(device).catch((e) => null));
        });

        // Return all valid FrontEnds
        return (this.pendingCall = Promise.all(devicesPromise)
            .then((x) => {
                // Filter undefined sound cameras
                this.pendingCall = null;
                return x.filter((x) => (x ? true : false));
            })
            .catch((x) => {
                this.pendingCall = null;
                throw x;
            }));
    }

    private async getAccessibleMeasurementDevice(measurementDevice: MeasurementDevice): Promise<MeasurementDevice> {
        const micIds: string[] = [];

        const remoteMeasurementDevice = MeasurementDevice.copy(measurementDevice);

        remoteMeasurementDevice.frontEnd.frontEndConnectors.forEach((x) => {
            x.microphoneArrayIds.forEach((y) => micIds.push(y));
        });

        // Fetch from WebApi
        return this.microphoneArrayRackModuleRepository
            .getMicrophoneArrayRack(micIds)
            .then((x) => {
                // We found a valid SoundCamera, so try to fetch the remaining stuff.

                // Return the original FrontEnd, with new fetched properties.
                remoteMeasurementDevice.frontEnd.name = x.HardwareId;
                remoteMeasurementDevice.microphoneArrayRackIds = { [x.HardwareId]: micIds };

                // Save a mapping of the FrontEndId to the moduleId, to save work later on.
                this.frontEndIdToModuleIds[remoteMeasurementDevice.frontEnd.id] = x.HardwareId;

                // Get calibration files
                const result: Promise<void>[] = [];

                for (const connector of remoteMeasurementDevice.frontEnd.frontEndConnectors) {
                    connector.sensorCalibrations = [];

                    result.push(this.dmadaqModuleRepository.getModule(connector.id).then((x) => {}));

                    // Fetch CaptureDevices
                    result.push(
                        this.getCaptureDevicesForMeasurementDevice(remoteMeasurementDevice).then((captureDevices) => {
                            remoteMeasurementDevice.captureDevices = captureDevices;
                        })
                    );

                    // Return the measurement device
                    return Promise.all(result).then(() => remoteMeasurementDevice);
                }
            })
            .catch((error) => {
                throw new Error(error.message);
            });
    }

    private getCaptureDevicesForMeasurementDevice(measurementDevice: MeasurementDevice): Promise<CaptureDevice[]> {
        // Check if ModuleId is available
        const moduleId: string = this.frontEndIdToModuleIds[measurementDevice.frontEnd.id];

        if (!moduleId) throw new Error("ModuleId unknown. Fetch FrontEnd first.");

        const connectedCaptureDevices =
            ManagementHelperFunctions.filterCaptureDevicesForMeasurementDevice(measurementDevice);

        return this.microphoneArrayRackModuleRepository.getWebcams(moduleId).then((webcams) => {
            const registeredWebCams: CaptureDevice[] = [];

            for (const webcam of webcams) {
                let captureDevice = connectedCaptureDevices.find(
                    (x) => x.vendorId == webcam.VendorId && x.productId == webcam.DeviceId
                );

                if (!captureDevice) {
                    captureDevice = <CaptureDevice>{};
                }

                registeredWebCams.push(ManagementHelperFunctions.webcamToCaptureDevice(webcam, captureDevice));
            }

            return registeredWebCams;
        });
    }
}
